import { useCallback, useMemo, useState } from "react";
import {
  ReactFlow,
  Node,
  Edge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
  MarkerType,
  Handle,
  Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { IEntity } from "@/types/breeze/Entity";
import { Badge, Box, Flex, Text } from "@radix-ui/themes";
import { Database, Key } from "lucide-react";
import FloatingEdge from "./FloatingEdge";

interface EntityERDProps {
  entities: IEntity[];
  onEntityClick?: (entityName: string) => void;
}

interface EntityNodeData extends Record<string, unknown> {
  entity: IEntity;
  onEntityClick?: (entityName: string) => void;
}

function EntityNode({ data }: { data: EntityNodeData }) {
  const { entity, onEntityClick } = data;
  const [isExpanded, setIsExpanded] = useState(false);
  const maxFieldsToShow = isExpanded ? entity.fields.length : 10;

  const handleNodeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleEntityNameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEntityClick?.(entity.name);
  };

  const handleViewMoreClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <Box
      style={{
        background: "var(--color-panel)",
        border: "1px solid var(--gray-a6)",
        borderRadius: "var(--radius-3)",
        minWidth: "240px",
        boxShadow: "var(--shadow-3)",
        position: "relative",
      }}
      onClick={handleNodeClick}
    >
      <Handle type="source" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Left} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Right} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />

      <Flex
        direction="column"
        gap="1"
        p="3"
        style={{
          background: "var(--accent-3)",
          borderBottom: "1px solid var(--gray-a6)",
          borderTopLeftRadius: "var(--radius-3)",
          borderTopRightRadius: "var(--radius-3)",
        }}
      >
        <Flex align="center" gap="2">
          <Database style={{ width: "16px", height: "16px" }} />
          <Text
            size="3"
            weight="bold"
            style={{ cursor: onEntityClick ? "pointer" : "default" }}
            onClick={handleEntityNameClick}
          >
            {entity.name}
          </Text>
        </Flex>
        <Flex gap="1" wrap="wrap">
          {entity.enable_rls && (
            <Badge size="1" variant="soft">
              RLS
            </Badge>
          )}
          {entity.is_junction && (
            <Badge size="1" variant="soft" color="orange">
              Junction
            </Badge>
          )}
          {entity.is_public && (
            <Badge size="1" variant="soft" color="green">
              Public
            </Badge>
          )}
        </Flex>
      </Flex>

      <Box p="2">
        <Flex direction="column" gap="1">
          {entity.fields.slice(0, maxFieldsToShow).map((field) => (
            <Flex
              key={field.id}
              align="center"
              gap="2"
              px="2"
              py="1"
              style={{
                fontSize: "12px",
                background: field.is_required
                  ? "var(--gray-a2)"
                  : "transparent",
                borderRadius: "var(--radius-2)",
              }}
            >
              {field.relationship && (
                <Key style={{ width: "12px", height: "12px", flexShrink: 0 }} />
              )}
              <Text
                size="1"
                weight={field.is_name_field ? "bold" : "regular"}
                style={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {field.name}
              </Text>
              <Text
                size="1"
                style={{
                  flexShrink: 0,
                  color: "var(--gray-11)",
                }}
              >
                {field.database_type}
              </Text>
            </Flex>
          ))}
          {entity.fields.length > 10 && (
            <Text
              size="1"
              color="blue"
              align="center"
              mt="1"
              style={{ cursor: "pointer" }}
              onClick={handleViewMoreClick}
            >
              {isExpanded
                ? "Show less"
                : `+${entity.fields.length - 10} more fields`}
            </Text>
          )}
        </Flex>
      </Box>
    </Box>
  );
}

const nodeTypes = {
  entity: EntityNode,
};

const edgeTypes = {
  floating: FloatingEdge,
};

export default function EntityERD({ entities, onEntityClick }: EntityERDProps) {
  // Calculate layout using a simple grid-based approach
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    // Separate main entities and junction entities
    const mainEntities = entities.filter((e) => !e.is_junction);
    const junctionEntities = entities.filter((e) => e.is_junction);
    const allEntities = [...mainEntities, ...junctionEntities];

    // Create nodes with grid layout
    const columns = Math.ceil(Math.sqrt(allEntities.length));
    const spacing = { x: 420, y: 380 };

    const nodes: Node<EntityNodeData>[] = allEntities.map((entity, index) => {
      const col = index % columns;
      const row = Math.floor(index / columns);

      const nodeId = String(entity.id);

      return {
        id: nodeId,
        type: "entity",
        position: {
          x: col * spacing.x + 50,
          y: row * spacing.y + 50,
        },
        data: {
          entity,
          onEntityClick,
        },
      };
    });

    const edges: Edge[] = [];
    const edgeSet = new Set<string>();

    allEntities.forEach((entity) => {
      entity.fields.forEach((field) => {
        if (!field.relationship) return;

        const rel = field.relationship;
        const targetEntity = allEntities.find(
          (e) => e.id === rel.target_entity_id,
        );
        if (!targetEntity) {
          return;
        }

        const sourceNodeId = String(entity.id);
        const targetNodeId = String(targetEntity.id);
        const edgeId = `${sourceNodeId}-${targetNodeId}-${field.name}`;
        if (edgeSet.has(edgeId)) return;
        edgeSet.add(edgeId);

        let color = "var(--gray-a8)";
        let strokeWidth = 2;

        switch (rel.type) {
          case "one-to-many":
            color = "var(--blue-9)";
            break;
          case "many-to-one":
            color = "var(--violet-9)";
            break;
          case "many-to-many":
            color = "var(--orange-9)";
            strokeWidth = 3;
            break;
          case "one-to-one":
            color = "var(--green-9)";
            break;
        }

        edges.push({
          id: edgeId,
          source: sourceNodeId,
          target: targetNodeId,
          type: "floating",
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color,
          },
          style: {
            stroke: color,
            strokeWidth,
          },
          animated: rel.type === "many-to-many",
          data: {
            label: field.name,
            relationshipType: rel.type,
          },
        });
      });
    });

    return { nodes, edges };
  }, [entities, onEntityClick]);

  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges);

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node<EntityNodeData>) => {
      if (onEntityClick && node.data) {
        onEntityClick(node.data.entity.name);
      }
    },
    [onEntityClick],
  );

  return (
    <Box
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        background: "var(--color-panel)",
      }}
    >
      <style>{`
        .react-flow__controls {
          background: var(--color-panel);
          border: 1px solid var(--gray-a6);
          border-radius: var(--radius-3);
          box-shadow: var(--shadow-3);
        }
        .react-flow__controls-button {
          background: var(--color-panel);
          border-bottom: 1px solid var(--gray-a6);
          color: var(--gray-12);
        }
        .react-flow__controls-button:hover {
          background: var(--gray-a3);
        }
        .react-flow__controls-button svg {
          fill: var(--gray-11);
        }
      `}</style>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
        <Controls />
      </ReactFlow>

      {/* Legend */}
      <Box
        style={{
          position: "absolute",
          bottom: "20px",
          right: "20px",
          background: "var(--color-panel)",
          border: "1px solid var(--gray-a6)",
          borderRadius: "var(--radius-3)",
          padding: "var(--space-3)",
          boxShadow: "var(--shadow-3)",
          zIndex: 10,
        }}
      >
        <Text size="2" weight="bold" mb="2">
          Relationships
        </Text>
        <Flex direction="column" gap="2">
          <Flex align="center" gap="2">
            <Box
              style={{
                width: "24px",
                height: "2px",
                background: "var(--blue-9)",
              }}
            />
            <Text size="1">One-to-Many</Text>
          </Flex>
          <Flex align="center" gap="2">
            <Box
              style={{
                width: "24px",
                height: "2px",
                background: "var(--violet-9)",
              }}
            />
            <Text size="1">Many-to-One</Text>
          </Flex>
          <Flex align="center" gap="2">
            <Box
              style={{
                width: "24px",
                height: "3px",
                background: "var(--orange-9)",
              }}
            />
            <Text size="1">Many-to-Many</Text>
          </Flex>
          <Flex align="center" gap="2">
            <Box
              style={{
                width: "24px",
                height: "2px",
                background: "var(--green-9)",
              }}
            />
            <Text size="1">One-to-One</Text>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
}
